import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Home, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Helmet } from 'react-helmet-async';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Success() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const sessionId = searchParams.get('session_id');

  // Add noindex meta tag
  useEffect(() => {
    const metaRobots = document.querySelector('meta[name="robots"]');
    if (metaRobots) {
      metaRobots.setAttribute('content', 'noindex, nofollow');
    }
  }, []);

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!sessionId) {
        setStatus('error');
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/stripe/checkout/status/${sessionId}`);
        const data = await response.json();

        if (data.success && data.payment_status === 'paid') {
          setStatus('success');
          setPaymentData(data);
        } else if (data.payment_status === 'unpaid') {
          setStatus('pending');
          setPaymentData(data);
        } else {
          setStatus('error');
        }
      } catch (error) {
        setStatus('error');
      }
    };

    checkPaymentStatus();
  }, [sessionId]);

  return (
    <>
      <Helmet>
        <title>Payment Status | Top Driver Driving School Liverpool</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <main className="min-h-[70vh] flex items-center justify-center px-4 py-16 bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="w-16 h-16 mx-auto text-red-500 animate-spin mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Processing Payment...
              </h1>
              <p className="text-gray-600">
                Please wait while we confirm your payment.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-20 h-20 mx-auto text-green-500 mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Payment Successful!
              </h1>
              <p className="text-gray-600 mb-6">
                Thank you for booking your Intensive Automatic Crash Course with Top Driver!
              </p>
              
              {paymentData && (
                <div className="bg-green-50 rounded-lg p-4 mb-6 text-left">
                  <p className="text-sm text-gray-700">
                    <strong>Amount Paid:</strong> £{paymentData.amount?.toFixed(2)}
                  </p>
                </div>
              )}

              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                <h3 className="font-semibold text-gray-900 mb-2">What happens next?</h3>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>✓ You'll receive a confirmation email shortly</li>
                  <li>✓ Our team will contact you within 24 hours</li>
                  <li>✓ We'll schedule your first lesson at a time that suits you</li>
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button asChild className="flex-1 bg-red-500 hover:bg-red-600">
                  <Link to="/">
                    <Home className="w-4 h-4 mr-2" />
                    Back to Home
                  </Link>
                </Button>
                <Button asChild variant="outline" className="flex-1">
                  <Link to="/contact">
                    <Phone className="w-4 h-4 mr-2" />
                    Contact Us
                  </Link>
                </Button>
              </div>
            </>
          )}

          {status === 'pending' && (
            <>
              <Loader2 className="w-16 h-16 mx-auto text-yellow-500 mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Payment Pending
              </h1>
              <p className="text-gray-600 mb-6">
                Your payment is being processed. This may take a few moments.
              </p>
              <Button asChild className="bg-red-500 hover:bg-red-600">
                <Link to="/">
                  <Home className="w-4 h-4 mr-2" />
                  Back to Home
                </Link>
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="w-20 h-20 mx-auto text-red-500 mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Payment Issue
              </h1>
              <p className="text-gray-600 mb-6">
                There was an issue with your payment. Please try again or contact us for assistance.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button asChild className="flex-1 bg-red-500 hover:bg-red-600">
                  <Link to="/">
                    <Home className="w-4 h-4 mr-2" />
                    Back to Home
                  </Link>
                </Button>
                <Button asChild variant="outline" className="flex-1">
                  <Link to="/contact">
                    <Phone className="w-4 h-4 mr-2" />
                    Contact Us
                  </Link>
                </Button>
              </div>
            </>
          )}
        </div>
      </main>
    </>
  );
}
